import React, { FC } from 'react';
import {
  Button,
  Icon,
  RemoveAlertDialog,
  useDisclosure,
} from '@wegroup/design-system';
import { useTranslation } from 'react-i18next';
import useflowsProductBasketStore from '@flows/src/store/zustand/flows-products/flowsProductBasketStore';
import { useBasketCalculation } from '@flows/src/components/flow-products/quotes/basket/BasketCalculationProvider';
import FlowProductsCalculateError from '@flows/src/pages/flow-products/FlowProductsCalculateError';

const BasketCalculateButton: FC = () => {
  const { isOpen, onClose, onOpen } = useDisclosure();
  const { t } = useTranslation();

  const packages = useflowsProductBasketStore((state) => state.packages);
  const insurances = useflowsProductBasketStore((state) => state.insurances);

  const { calculate, isCalculating, error } = useBasketCalculation();

  const handleRecalculate = async () => {
    try {
      await calculate({
        chosen_insurance_packages: packages.map((pkg) => ({
          ...pkg,
          name: pkg.name,
          code: pkg.code,
          chosen_insurance_policies: pkg.quotes.map((quote) => ({
            anva_product_id: quote.anva_product_id,
            index: quote.index,
            coverages: quote.coverages
              .filter(({ selected }) => selected)
              .map(({ code }) => Number(code)),
          })),
        })),
        chosen_insurance_policies: insurances.map((quote) => ({
          anva_product_id: quote.anva_product_id,
          index: quote.index,
          coverages: quote.coverages
            .filter(({ selected }) => selected)
            .map(({ code }) => Number(code)),
          due_date: quote.start_date,
          payment_interval: quote.payment_interval ?? undefined,
        })),
      });
    } catch (calcError) {
      console.error('Calculation error:', calcError);
      onOpen();
    }
  };

  // Auto-open error modal when there's an error
  React.useEffect(() => {
    if (error && !isCalculating) {
      onOpen();
    }
  }, [error, isCalculating, onOpen]);

  return (
    <>
      <RemoveAlertDialog
        isOpen={isOpen}
        onClose={onClose}
        onConfirm={onClose}
        title={t('Calculation failed')}
        hideCancelButton
        confirmButtonText={t('CLOSE')}
        size="2xl"
      >
        <FlowProductsCalculateError error={error || undefined} />
      </RemoveAlertDialog>
      <Button
        variant="secondary"
        onClick={handleRecalculate}
        isLoading={isCalculating}
        leftIcon={<Icon name="GeneralActionsRefreshOrReload" />}
      >
        {t('Re-calculate')}
      </Button>
    </>
  );
};

export default BasketCalculateButton;
